import { ICONS } from '~/constants/icons';
import { 
  getCategoryText, 
  getLivingSpace, 
  getMonthlyFee, 
  getPlotSize,
  getOperationCost,
  mapCategory,
} from '@habity/services';
import { DataFieldConfig, forEstateTypes } from './data-builder';


export const generalFields: DataFieldConfig[] = [
  {
    key: 'monthly_fee',
    label: 'Avgift',
    icon: ICONS.MONTHLY_FEE,
    source: 'address',
    formatter: (value) => getMonthlyFee(value),
    condition: (context) => {
      const value = context.address?.monthly_fee;
      return value && value > 0;
    },
  },

  // Category with special handling for new construction
  {
    key: 'category',
    label: 'Boendetyp',
    icon: ICONS.CATEGORY,
    source: 'address',
    estateTypes: forEstateTypes('House', 'Condominium', 'Cottage', 'Farm', 'HousingCooperative'),
    formatter: (value, context) => {
      if (value === 'new_construction') {
        let categoryName = getCategoryText(value);
        if (context?.filteredProviderData && context?.provider_type) {
          const categoryExcludingNewProduction = mapCategory(
            context.filteredProviderData, 
            context.provider_type, 
            true
          );
          categoryName = categoryExcludingNewProduction 
            ? `${getCategoryText(categoryExcludingNewProduction)} (Nyproduktion)` 
            : categoryName;
        }
        return categoryName;
      }
      return getCategoryText(value);
    },
  },

    {
    key: 'category',
    label: 'Typ',
    icon: ICONS.CATEGORY,
    source: 'address',
    estateTypes: forEstateTypes('Plot', 'CommercialProperty', 'Project'),
    formatter: (value) => {
      return getCategoryText(value);
    },
  },

  {
    key: 'rooms',
    label: 'Antal rum',
    icon: ICONS.ROOMS,
    source: 'address',
    formatter: (value) => `${value} rum och kök`,
  },

  {
    key: 'mainResidentialBuilding.numberOfRooms',
    label: 'Antal rum',
    icon: ICONS.ROOMS,
    formatter: (value) => `${value} rum och kök`,
  },


  {
    key: 'mainResidentialBuilding.numberOfBedRooms.minValue',
    label: 'Varav sovrum',
    formatter: (value, context) => 
      `${value}${context.filteredProviderData?.mainResidentialBuilding?.numberOfBedRooms?.maxValue ? `-${context.filteredProviderData?.mainResidentialBuilding?.numberOfBedRooms?.maxValue}` : ''} st`,
  },


  {
    key: 'bedrooms',
    label: 'Varav sovrum',
    icon: ICONS.BEDROOMS,
    source: 'address',
    condition: (context) => {
      const value = context.address?.bedrooms;
      return value && context.address?.rooms;
    },
    formatter: (value, context) => 
      `${value}${context.address?.bedrooms_max && context.address?.bedrooms_max > value ? `-${context.address.bedrooms_max}` : ''} st`,
  },

  {
    key: 'living_space',
    label: 'Boarea',
    icon: ICONS.LIVING_SPACE,
    source: 'address',
    estateTypes: forEstateTypes('House', 'Cottage', 'HousingCooperative', 'Condominium'),
    formatter: (value, context) => {
      const otherSpace = context.address?.additional_space;
      return getLivingSpace(value, otherSpace)
    },
  },

  
  {
    key: 'mainResidentialBuilding.livingSpace',
    label: 'Boarea',
    icon: ICONS.LIVING_SPACE,
    estateTypes: forEstateTypes('Farm'),
    formatter: (value, context) => {
      const otherSpace = context.filteredProviderData?.mainResidentialBuilding?.otherSpace;
      return getLivingSpace(value, otherSpace)
    },
  },

  // Commercial property total area
  {
    key: 'compilationAreas.area',
    label: 'Total yta',
    icon: ICONS.LIVING_SPACE,
    estateTypes: forEstateTypes('CommercialProperty'),
    formatter: (value) => `${Number(value).toLocaleString('sv-SE')} m²`,
    condition: (context) => {
      const value = context.filteredProviderData?.compilationAreas?.area;
      return value && value > 0;
    },
  },

    {
    key: 'propertyTypeAndAreas.area',
    label: 'Markareal',
    condition: (context) => {
      const value = context.filteredProviderData?.propertyTypeAndAreas?.area;
      return value && value > 0;
    },
    formatter: (value) => `${Number(value).toFixed(3)} hektar`,
  },



  {
    key: 'plot_size',
    label: 'Tomtstorlek',
    icon: ICONS.PLOT_SIZE,
    source: 'address',
    formatter: (value) => getPlotSize(value),
  },

  // Housing cooperative floor information
  {
    key: 'floorAndElevator.floor',
    label: 'Våning',
    icon: ICONS.FLOOR,
    estateTypes: forEstateTypes('HousingCooperative'),
    condition: (context) => {
      const floorData = context.filteredProviderData?.floorAndElevator;
      return floorData?.floor !== undefined || 
             floorData?.totalNumberOfFloors !== undefined || 
             floorData?.floorCommentary !== undefined;
    },
    formatter: (value, context) => {
      const { floor, totalNumberOfFloors, floorCommentary } = 
        context.filteredProviderData?.floorAndElevator || {};
      return `${floor !== undefined && totalNumberOfFloors ? `${floor} av ${totalNumberOfFloors}` : floor !== undefined ? floor : ''}${floorCommentary ? ` (${floorCommentary})` : ''}`;
    },
  },

  // Build year with provider data override
   {
    key: 'building.buildingYear',
    label: 'Byggår',
    icon: ICONS.BUILDING.YEAR,
    // estateTypes: forEstateTypes('House', 'Cottage'),
    formatter: (value, context) => {
      const commentary = context?.filteredProviderData?.building?.commentaryForBuildingYear;
      return commentary ? `${value} (${commentary})` : value;
    },
  },

 {
    key: 'mainResidentialBuilding.yearBuilt',
    label: 'Byggår',
    icon: ICONS.BUILDING.YEAR,
    formatter: (value, context) => {
      const commentary = context?.filteredProviderData?.mainResidentialBuilding?.building?.commentaryForBuildingYear;
      return commentary ? `${value} (${commentary})` : value;
    },
  },


  // Property designation (only shown when show_address is true)
  {
    key: 'property_designation',
    label: 'Fastighetsbeteckning',
    icon: ICONS.PROPERTY_DESIGNATION,
    source: 'address',
    condition: (context) => {
      const value = context.address?.property_designation;
      return value && context.home?.status?.show_address;
    },
  },

  // Commercial property building year from first building
  {
    key: 'buildings[0].yearBuilt',
    label: 'Byggår',
    icon: ICONS.BUILDING.YEAR,
    estateTypes: forEstateTypes('CommercialProperty'),
    formatter: (value, context) => {
      const firstBuilding = context?.filteredProviderData?.buildings?.[0];
      const commentary = firstBuilding?.buildingYearComment;
      return commentary ? `${value} (${commentary})` : value;
    },
    getValue: (context) => {
      return context?.filteredProviderData?.buildings?.[0]?.yearBuilt;
    },
  },

  // Energy declaration fields
  {
    key: 'energyDeclaration.energyClass',
    label: 'Energiklass',
    icon: ICONS.OPERATING_COSTS.ELECTRICITY, // Using electricity icon as fallback
    // estateTypes: forEstateTypes('House', 'Cottage', 'HousingCooperative'),
  },


  {
    key: 'compilationAreas.rentIncomes',
    label: 'Intäkt',
    icon: ICONS.OPERATING_COSTS.SUM, // Using electricity icon as fallback: ;
    estateTypes: forEstateTypes('CommercialProperty'),
    formatter: (value) => `${getOperationCost(value, false)}`,
    condition: (context) => {
      const value = context.filteredProviderData?.compilationAreas?.rentIncomes;
      return value && value > 0;
    },
  },

  // Energy declaration fields
  {
    key: 'operation.sum',
    label: 'Driftkostnad',
    icon: ICONS.OPERATING_COSTS.SUM, // Using electricity icon as fallback: ;
    estateTypes: forEstateTypes('CommercialProperty'),
    formatter: (value) => `${getOperationCost(value, false)}`,
    condition: (context) => {
      const value = context.filteredProviderData?.operation?.sum;
      return value && value > 0;
    },
  },


];
